import React, { useMemo, useState } from 'react';
import { Link as RouterLink, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { useAuth } from '../../hooks/useAuth';
import { adminLoginPath, adminPath, adminRoute } from '../../config/adminPath';
import {
  adminDrawerHeaderSx,
  adminDrawerPaperSx,
  adminShellBackgroundSx,
  adminTopbarSx,
} from '../../components/admin/adminVisuals';

const drawerWidth = 280;

const navigationItems = [
  {
    label: 'Dashboard',
    to: adminPath,
    icon: <DashboardRoundedIcon />,
  },
  {
    label: 'Usuários administrativos',
    to: adminRoute('users'),
    icon: <GroupRoundedIcon />,
  },
  {
    label: 'Banners da página inicial',
    to: adminRoute('homepage/banners'),
    icon: <ImageRoundedIcon />,
  },
  {
    label: 'Popup promocional',
    to: adminRoute('homepage/popup'),
    icon: <CampaignRoundedIcon />,
  },
  {
    label: 'Oportunidades e editais',
    to: adminRoute('homepage/editais'),
    icon: <DescriptionRoundedIcon />,
  },
  {
    label: 'Documentos da Secad',
    to: adminRoute('secretaria/secad'),
    icon: <FolderRoundedIcon />,
  },
  {
    label: 'Documentos da Monitoria',
    to: adminRoute('secretaria/monitoria'),
    icon: <SchoolRoundedIcon />,
  },
  {
    label: 'Documentos e relatórios da CPA',
    to: adminRoute('cpa/documentos'),
    icon: <FactCheckRoundedIcon />,
  },
  {
    label: 'Serviços ao Aluno',
    to: adminRoute('secretaria/servicos-aluno'),
    icon: <FolderRoundedIcon />,
  },
  {
    label: 'Biblioteca',
    to: adminRoute('biblioteca/recursos'),
    icon: <DescriptionRoundedIcon />,
  },
  { label: 'Cursos de graduação', to: adminRoute('cursos'), icon: <SchoolRoundedIcon /> },
];

function NavigationList({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();

  return (
    <List sx={{ px: 1.5, py: 2.5 }}>
      {navigationItems.map((item) => {
        const isSelected =
          item.to === adminPath
            ? location.pathname === item.to
            : location.pathname.startsWith(item.to);

        return (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            selected={isSelected}
            onClick={onNavigate}
            sx={{
              borderRadius: 3,
              mb: 0.75,
              py: 1.1,
              color: '#30435a',
              transition: 'all 160ms ease',
              '&:hover': {
                bgcolor: 'rgba(37, 99, 235, 0.06)',
              },
              '&.active, &.Mui-selected': {
                bgcolor: 'rgba(37, 99, 235, 0.1)',
                color: '#0f3d66',
                boxShadow: 'inset 0 0 0 1px rgba(37, 99, 235, 0.12)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        );
      })}
    </List>
  );
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const { adminAccessStatus, adminProfile, signOut, user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = useMemo(() => {
    const email = user?.email ?? 'admin';
    return email.slice(0, 2).toUpperCase();
  }, [user?.email]);

  const handleLogout = async () => {
    const error = await signOut();

    if (error) {
      console.error('Falha ao encerrar sessão:', error);
      return;
    }

    navigate(adminLoginPath, { replace: true });
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={adminDrawerHeaderSx}>
        <Typography variant="overline" sx={{ opacity: 0.7, letterSpacing: 1.6, fontWeight: 700 }}>
          Faculdade Senac
        </Typography>
        <Typography variant="h6" fontWeight={900} sx={{ mt: 0.3 }}>
          Painel Administrativo
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'rgba(22, 50, 79, 0.82)' }}>
          Conteúdos e acessos organizados em um único ambiente.
        </Typography>
      </Box>

      <NavigationList onNavigate={() => setMobileOpen(false)} />

      <Box sx={{ mt: 'auto', p: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Button
          fullWidth
          color="inherit"
          component={RouterLink}
          to="/"
          startIcon={<HomeRoundedIcon />}
          sx={{ justifyContent: 'flex-start', mb: 1, borderRadius: 3 }}
        >
          Voltar ao site
        </Button>
        <Button
          fullWidth
          color="inherit"
          startIcon={<LogoutRoundedIcon />}
          onClick={handleLogout}
          sx={{ justifyContent: 'flex-start', borderRadius: 3 }}
        >
          Sair
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ ...adminShellBackgroundSx, display: 'flex' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          ...adminTopbarSx,
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: 'none' } }}
          >
            <MenuRoundedIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
              Administração do site
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
              Atualize conteúdos, acompanhe acessos e mantenha a rotina administrativa mais fluida.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={adminAccessStatus === 'authorized' ? 'Acesso confirmado' : 'Atenção necessária'}
              color={adminAccessStatus === 'authorized' ? 'success' : 'warning'}
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.76)', fontWeight: 700 }}
            />
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  color: '#0f3d66',
                  bgcolor: 'rgba(37, 99, 235, 0.12)',
                  border: '1px solid rgba(37, 99, 235, 0.12)',
                }}
              >
                {initials}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography fontWeight={800} sx={{ color: '#16324f' }}>
                  {adminProfile?.email || user?.email || 'Administrador'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {adminProfile?.accessMode === 'bootstrap' ? 'Acesso principal' : 'Sessão ativa'}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              ...adminDrawerPaperSx,
            },
          }}
        >
          {drawerContent}
        </Drawer>

        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              ...adminDrawerPaperSx,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        <Toolbar />
        <Box sx={{ p: { xs: 2, md: 4 }, position: 'relative' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
